/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState, xHeaderActions } from "@stoneage/App/Store/Reducers";
import { screen } from "@stoneage/XDL/Helpers";
import React, { FC, useEffect, useState } from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface LogoLinksSearchStyles {
    logoWrapper: InjectableCss;
    searchWrapper?: InjectableCss;
    containerContentWrapper?: InjectableCss;
    mainNavigationWrapper?: InjectableCss;
    navigationSearchWrapper?: InjectableCss;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    containerContentWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            height: 100%;
            width: 100%;
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            min-width: 5px;
            height: 44px;
            width: 224px;
            margin-right: 20px;
            display: flex;
            align-items: center;

            .home-link > span,
            img {
                max-width: 224px;
                max-height: 44px;
            }

            ${screen("lte", "md")`
                height: 28px;
                width: 182px;
                margin-right: 10px;
                .home-link >span, img {
                    max-width: 144px;
                    max-height: 28px;
                }
            `}

            @media (max-width: 768px) {
                ${({ searchInputFocus }: any) =>
                    searchInputFocus &&
                    `
                    max-width: 45px;
                    overflow: hidden;
                    img{
                        display: none;
                    }
                `};
            }
        `,
    },
    mainNavigationWrapper: {
        css: css`
            margin-right: 50px;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex: 1;
            ${({ searchInputFocus }: any) => searchInputFocus && `width:0;margin-right:0;height:0;visibility:hidden;`};

            @media (max-width: 1250px) and (min-width: 1025px) {
                margin-right: 15px;
            }

            ${screen("lte", "md")`display:none;`};
        `,
    },
    navigationSearchWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex: 1;
            align-items: center;
            ${screen("lte", "md")`  
                justify-content: flex-end;        
            `};
        `,
    },
    searchWrapper: {
        css: css`
            width: 100%;
            max-width: 198px;
            transition: 0.2s;
            &.searchInputFocus {
                max-width: 100%;
            }
            ${screen("lte", "md")`
                &, &.searchInputFocus {
                    max-width: 450px;
                }
            `};
            ${screen("lte", "sm")`
                    max-width: 352px; 
                    &.searchInputFocus {
                        max-width: 100%;
                    }
                `};
            ${screen("lte", "xs")`
                    max-width: 120px; 
                    &.searchInputFocus {
                        max-width: 100%;
                    }
                `};
        `,
    },
};

const mapStateToProps = (state: CustomAppState) => ({
    isSticky: state.xHeader.isSticky,
    searchInputFocus: state.xHeader.searchInputFocus,
    permissions: state.context.permissions,
    session: state.context.session,
    pageType: getCurrentPage(state).type, // HomePage
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & DispatchProp<xHeaderActions>;

const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };

const LogoLinksSearch: FC<Props> = ({ id, dispatch, isSticky, searchInputFocus, permissions, pageType, session }) => {
    return (
        <StyledWrapperX
            sticky={isSticky}
            {...logoLinksSearchStyles.containerContentWrapper}
            className="header_logo-links-search"
        >
            <StyledWrapperX
                sticky={isSticky}
                {...logoLinksSearchStyles.logoWrapper}
                searchInputFocus={searchInputFocus}
            >
                <Zone contentId={id} zoneName="LogoZone" />
            </StyledWrapperX>
            <StyledWrapperX
                sticky={isSticky}
                searchInputFocus={searchInputFocus}
                className="header_links-search"
                {...logoLinksSearchStyles.navigationSearchWrapper}
            >
                <StyledWrapperX
                    sticky={isSticky}
                    searchInputFocus={searchInputFocus}
                    {...logoLinksSearchStyles.mainNavigationWrapper}
                >
                    <Zone contentId={id} zoneName="MainNavigation" />
                </StyledWrapperX>
                <StyledWrapperX
                    sticky={isSticky}
                    searchInputFocus={searchInputFocus}
                    {...logoLinksSearchStyles.searchWrapper}
                    className={searchInputFocus ? " header_search searchInputFocus" : "header_search"}
                >
                    <Zone contentId={id} zoneName="SearchZone" />
                </StyledWrapperX>
            </StyledWrapperX>
        </StyledWrapperX>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
